import { useCallback, useEffect, useState } from 'react';

/**
 * Interface state of the shell: whether the sidebar is collapsed (kept in this browser, docs/05
 * §5.1) and whether the mobile drawer is open (never persisted). Small and local, so a context
 * plus `localStorage` is enough; no store until several screens share state.
 */

export const SIDEBAR_COLLAPSED_KEY = 'flow.sidebar.collapsed';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    // Private windows can refuse storage; the sidebar simply starts expanded.
    return false;
  }
}

export interface ShellState {
  collapsed: boolean;
  toggleCollapsed: () => void;
  menuOpen: boolean;
  openMenu: () => void;
  closeMenu: () => void;
}

export function useShellState(): ShellState {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      // Nothing to do: the choice just does not survive this session.
    }
  }, [collapsed]);

  return {
    collapsed,
    toggleCollapsed: useCallback(() => setCollapsed((value) => !value), []),
    menuOpen,
    openMenu: useCallback(() => setMenuOpen(true), []),
    closeMenu: useCallback(() => setMenuOpen(false), []),
  };
}
